import React, { useCallback, useEffect, useRef, useState } from "react";
import { ArrowTopRightOnSquareIcon, CheckCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";

interface EnableCheckoutExtensionReminderProps {
  shopDomain?: string;
}

function openShopifyCheckoutEditor(shopDomain: string, profileId?: string | null) {
  const storeSlug = shopDomain.replace('.myshopify.com', '');
  const profileIdToUse = profileId || '1977188539';

  return `https://admin.shopify.com/store/${storeSlug}/settings/checkout/editor/profiles/${profileIdToUse}?page=thank-you&context=apps`;
}

export function EnableCheckoutExtensionReminder({ shopDomain }: EnableCheckoutExtensionReminderProps) {
  const [isOpen, setIsOpen] = useState(shopDomain ? true : false);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const hasFetchedProfileRef = useRef(false);
  const mountedRef = useRef(true);

  const fetchProfileId = useCallback(async () => {
    if (!shopDomain) return;

    setIsLoadingProfile(true);
    try {
      const response = await fetch(`/api/get-checkout-profile?shop=${encodeURIComponent(shopDomain)}`);

      if (response.ok) {
        const result = await response.json();
        if (result.success && result.profileId) {
          if (!mountedRef.current) return;
          setProfileId(result.profileId);
        }
      } else {
        console.error("Failed to fetch checkout profile:", response.status);
      }
    } catch (error) {
      console.error("Error fetching checkout profile:", error);
    } finally {
      if (mountedRef.current) {
        setIsLoadingProfile(false);
      }
    }
  }, [shopDomain]);

  useEffect(() => {
    mountedRef.current = true;

    if (shopDomain) {
      setIsOpen(true);
      if (!hasFetchedProfileRef.current) {
        hasFetchedProfileRef.current = true;
        fetchProfileId();
      }
    }

    return () => {
      mountedRef.current = false;
    };
  }, [shopDomain, fetchProfileId]);

  if (!shopDomain || !isOpen) return null;

  const openCheckoutEditor = () => {
    if (!shopDomain) return;

    const storeSlug = shopDomain.replace('.myshopify.com', '');
    const profileIdToUse = profileId || '1977188539';
    const checkoutEditorUrl = `https://admin.shopify.com/store/${storeSlug}/settings/checkout/editor/profiles/${profileIdToUse}?page=thank-you&context=apps`;

    window.open(checkoutEditorUrl, '_blank', 'noopener,noreferrer');
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md transform transition-all">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>

          {/* Content */}
          <div className="p-6">
            <div className="text-center">
              {/* Icon */}
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-100 mb-4">
                <CheckCircleIcon className="h-8 w-8 text-blue-600" />
              </div>
              
              {/* Title */}
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Complete Your Setup
              </h3>
              
              {/* Description */}
              <p className="text-gray-600 mb-6">
                Add engaging games to your checkout thank you page to increase customer engagement 
                and drive repeat purchases.
              </p>
              
              {/* Action buttons */}
              <div className="space-y-3">
                <button
                  onClick={openCheckoutEditor}
                  disabled={isLoadingProfile}
                  className="w-full bg-blue-600 text-white px-4 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                  {isLoadingProfile ? "Preparing..." : "Setup Checkout Extension"}
                </button>
                
                <button
                  onClick={handleClose}
                  className="w-full text-gray-500 hover:text-gray-700 text-sm underline"
                >
                  I'll do this later
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
